import type { AuthSession, AuthTokens, Permission, Role } from "@oms/types";
import { create } from "zustand";
import { persist } from "zustand/middleware";

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: AuthSession["user"] | null;
  organization: AuthSession["organization"] | null;
  role: Role | null;
  /** Precise role held in the active org — may be a custom role. */
  roleId: string | null;
  roleName: string | null;
  /** Effective permissions; the UI renders from these, not from `role`. */
  permissions: Permission[];
  isSuperAdmin: boolean;
  isAuthenticated: () => boolean;
  setAuth: (tokens: AuthTokens, session: AuthSession) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setSession: (session: AuthSession) => void;
  clearAuth: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      organization: null,
      role: null,
      roleId: null,
      roleName: null,
      permissions: [],
      isSuperAdmin: false,
      isAuthenticated: () => Boolean(get().accessToken),
      setAuth: (tokens, session) =>
        set({
          accessToken: tokens.accessToken,
          refreshToken: tokens.refreshToken,
          user: session.user,
          organization: session.organization ?? null,
          role: session.role ?? null,
          roleId: session.roleId ?? null,
          roleName: session.roleName ?? null,
          permissions: session.permissions ?? [],
          isSuperAdmin: session.isSuperAdmin ?? false,
        }),
      setTokens: (accessToken, refreshToken) =>
        set({ accessToken, refreshToken }),
      setSession: (session) =>
        set({
          user: session.user,
          organization: session.organization ?? null,
          role: session.role ?? null,
          roleId: session.roleId ?? null,
          roleName: session.roleName ?? null,
          permissions: session.permissions ?? [],
          isSuperAdmin: session.isSuperAdmin ?? false,
        }),
      clearAuth: () =>
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          organization: null,
          role: null,
          roleId: null,
          roleName: null,
          permissions: [],
          isSuperAdmin: false,
        }),
    }),
    {
      name: "oms-auth",
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        organization: state.organization,
        role: state.role,
        roleId: state.roleId,
        roleName: state.roleName,
        permissions: state.permissions,
        isSuperAdmin: state.isSuperAdmin,
      }),
    },
  ),
);

export function getAccessToken() {
  return useAuthStore.getState().accessToken;
}

export function getRefreshToken() {
  return useAuthStore.getState().refreshToken;
}
